import { useTranslation } from "react-i18next";

import MetricSelector from "common/components/MetricSelector";
import RecentLeads from "common/components/RecentLeads";
import RestrictedComponent from "common/components/RestrictedComponent";
import StatisticsGraph from "common/components/StatisticsGraph";
import TimeframeSelector from "common/components/TimeframeSelector";
import useMetrics from "common/datahooks/useMetrics";
import useSelectedMetric from "common/hooks/useSelectedMetric";
import useSelectedTimeframe from "common/hooks/useSelectedTimeframe";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../../constants";
import { useCampaignContext } from "../../../../context/CampaignContext";
import useCampaignMetrics from "../../../../datahooks/useCampaignMetrics";
import AutoLabels from "../../AutoLabels";
import SectionHeading from "../../SectionHeading";
import CampaignStats from "./CampaignStats";
import FlowsOverview from "./FlowsOverview";

import type { CampaignFlow } from "../../../../types";

interface CampaignOverviewProps {
  flows: CampaignFlow[];
  openLeadPreview: (leadId: string) => void;
}

const timeframeLocalStorageKey = "campaign-timeframe";

export default function CampaignOverview({
  flows,
  openLeadPreview,
}: CampaignOverviewProps) {
  const { t } = useTranslation("campaigns");

  const campaign = useCampaignContext();
  const {
    id: campaignId,
    owners,
    outreach_type: outreachType,
    inmail_optimization: isInMailOptimizationEnabled,
    labels,
  } = campaign;

  const { selectedTimeframe, setSelectedTimeframe, selectedTimeframeOption } =
    useSelectedTimeframe(
      JSON.parse(localStorage.getItem(timeframeLocalStorageKey)),
    );

  const { campaignMetrics, isLoadingCampaignMetrics } =
    useCampaignMetrics(campaignId);

  const {
    metrics,
    metricsSum,
    refetchMetrics,
    metricsError,
    isLoadingMetrics,
  } = useMetrics({
    campaign,
    selectedTimeframe,
    accountIds: [],
  });

  const isDrip = outreachType === "drip";
  const isLoadingMetricsData = isLoadingMetrics || isLoadingCampaignMetrics;

  const { hiddenMetricsColumns } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];

  // Hide metrics column based on InMail optimization
  if (!isInMailOptimizationEnabled) {
    hiddenMetricsColumns.push("sent_inmails");
  }

  const { setSelectedMetric, selectedMetric } = useSelectedMetric({
    hiddenMetrics: hiddenMetricsColumns,
  });

  return (
    <>
      <section className="flex flex-col justify-between gap-4 lg:flex-row items-start">
        <SectionHeading
          title={t("campaignDetails.analyticsTitle")}
          subtitle={t("campaignDetails.analyticsSubtitle")}
        />

        <TimeframeSelector
          shouldOffsetTimezone
          className="max-lg:ml-auto"
          selectedTimeframe={selectedTimeframe}
          selectTimeframe={(selection) => {
            setSelectedTimeframe(selection);
            localStorage.setItem(
              timeframeLocalStorageKey,
              JSON.stringify(selection),
            );
          }}
        />
      </section>

      <CampaignStats />

      <div className="flex flex-col gap-y-2.5">
        <MetricSelector
          metricsSum={metricsSum}
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          totalMetrics={campaignMetrics}
          isLoading={isLoadingMetricsData}
          hiddenColumns={hiddenMetricsColumns}
        />
        <StatisticsGraph
          selectedMetric={selectedMetric}
          metrics={metrics}
          metricsSum={metricsSum}
          requestError={metricsError}
          isLoading={isLoadingMetricsData}
          isHourly={
            selectedTimeframeOption.getParameters({ campaign }).bucketSize ===
            "1 hour"
          }
          refetchMetrics={refetchMetrics}
        />
      </div>

      <FlowsOverview flows={flows} selectedTimeframe={selectedTimeframe} />

      <RestrictedComponent visibleForPermissions={["leads_full"]}>
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <SectionHeading
            title={`Recent ${isDrip ? "replies" : "leads"}`}
            subtitle={`The latest ${isDrip ? "responses" : "leads"} you have gained
              through this campaign`}
          />
          <AutoLabels campaignId={campaignId} labels={labels} />
        </div>

        <RecentLeads
          campaignId={campaignId}
          accountIds={owners}
          isDrip={isDrip}
          openPreviewSidebar={openLeadPreview}
        />
      </RestrictedComponent>
    </>
  );
}
